import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import StickyBottomNav from "../../components/Navbar/StickyBottomNav";
import { ArrowRight, BookOpen, Heart, MessageSquare, Users, Clock, Award } from 'lucide-react';

// Import the same images from WhatWeDoHome component
import AdvocacyPhoto from '../../images/Health_WWD.jpg';
import ActionPhoto from "../../images/Peace_WWD.JPG";
import EducationPhoto from "../../images/Hero_WWD.jpg";

const ImpactStatCard = ({ icon, number, description }) => (
  <motion.div 
    className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center"
    whileHover={{ y: -5 }}
    transition={{ duration: 0.2 }}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="font-display text-h2 text-cta mb-2">{number}</h3>
    <p className="text-textPrimary text-bodyRegular">{description}</p>
  </motion.div>
);

// Custom Link component that scrolls to top when clicked
const ScrollToTopLink = ({ to, children, className }) => {
  const handleClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <Link to={to} onClick={handleClick} className={className}>
      {children}
    </Link>
  );
};

const PillarSection = ({ title, image, icon, children, stat, alignment = "left" }) => {
  return (
    <motion.div 
      className="mb-20"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className={`flex flex-col ${alignment === "right" ? "md:flex-row-reverse" : "md:flex-row"} gap-8`}>
        <div className="md:w-1/2">
          <div className="relative rounded-xl overflow-hidden shadow-md h-64 md:h-80 lg:h-96">
            <img 
              src={image} 
              alt={title} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 to-transparent"></div>
            <div className="absolute bottom-0 left-0 p-6">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center mb-3">
                {icon}
              </div>
              <h3 className="font-display text-h2 text-white">{title}</h3>
            </div>
          </div>
        </div>
        
        <div className="md:w-1/2">
          <div className="prose max-w-none mb-6">
            {children}
          </div>
          {stat && (
            <div className="bg-lightTeal rounded-lg p-4 inline-flex items-center">
              <span className="font-display text-h3 text-cta mr-3">{stat.number}</span>
              <span className="text-textPrimary">{stat.description}</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

function Impact() {
  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">
              OUR IMPACT
            </h1>
            <div className="w-16 h-1 bg-cta mx-auto mb-6"></div>
            
            <p className="text-bodyLarge text-textPrimary max-w-3xl mx-auto mb-8">
              Through Education, Action, and Advocacy, we create lasting impact in refugee communities.
            </p>
            
            {/* Coming Soon Banner */}
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-md max-w-3xl mx-auto mb-16">
              <h2 className="font-display text-h3 text-secondary mb-4">Detailed Impact Report Coming Soon</h2>
              <p className="text-textPrimary mb-6">
                We're currently gathering comprehensive data and stories about our impact on
                refugee communities around the world. Check back soon to see detailed metrics on how 
                your support is making a tangible difference.
              </p>
              <div className="w-16 h-1 bg-cta mx-auto"></div>
            </div>
          </motion.div>
          
          {/* Impact Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            <ImpactStatCard 
              icon={<BookOpen size={32} className="text-primary" />}
              number="1,000+" 
              description="Hours of Tutoring Provided"
            />
            
            <ImpactStatCard 
              icon={<Users size={32} className="text-primary" />}
              number="4,000+" 
              description="Refugees Supported Worldwide"
            />
            
            <ImpactStatCard 
              icon={<Award size={32} className="text-primary" />}
              number="100+" 
              description="Active Members Making a Difference"
            />
          </div>
          
          {/* What We Do Sections */}
          <section className="mb-16">
            <PillarSection 
              title="Education" 
              image={EducationPhoto}
              icon={<BookOpen size={24} className="text-white" />}
              stat={{ number: "1,500+", description: "Tutoring Hours" }}
            >
              <p className="text-textPrimary text-bodyRegular">
                At Students for Refugees, we believe education is a cornerstone of empowerment. Our mission begins in classrooms and lecture halls, where we shine a light on the realities of the global refugee crisis. Each chapter is charged with the responsibility of creating and delivering lectures on pressing issues that shape the lives of refugees worldwide. Through these efforts, we cultivate a generation of students who are not only informed but who carry the cause of advocacy with them wherever they go.
              </p>
              <p className="text-textPrimary text-bodyRegular">
                But education doesn't stop at awareness—it thrives in action. In 2023, we launched a tutoring program with a small cohort of 15 refugee children, aiming to meet their academic needs. Today, that program has grown exponentially, with over 50 tutors providing more than 1,500 hours of personalized assistance. These moments are not one-sided; they are an exchange. Our student tutors learn the rich histories and cultures of their students, while refugee children gain the tools they need to navigate and thrive in a new world.
              </p>
            </PillarSection>
            
            <PillarSection 
              title="Action" 
              image={ActionPhoto}
              icon={<Heart size={24} className="text-white" />}
              alignment="right"
              stat={{ number: "100%", description: "Zero-Overhead Donations" }}
            >
              <p className="text-textPrimary text-bodyRegular">
                We are not content to work from the sidelines. At Students for Refugees, we believe in the power of direct action. One of our most impactful initiatives is the delivery of essential goods—non-perishable food, clothing, and hygiene kits—straight to the doorsteps of vulnerable refugee families. Funded entirely through donations and bolstered by our zero-overhead policy, these efforts reflect our commitment to transparency and tangible impact.
              </p>
              <p className="text-textPrimary text-bodyRegular">
                In Houston, Texas, we lend our hands and hearts to the Refugee Peace Farm. Here, refugee women—many of whom once tilled the land in their home countries—regain the tools and space to cultivate a new sense of belonging. Together, we help to maintain the community farm, nurturing not only crops but here the students who partake learn the resilience and spirit of those who work the soil.
              </p>
            </PillarSection>
            
            <PillarSection 
              title="Advocacy" 
              image={AdvocacyPhoto}
              icon={<MessageSquare size={24} className="text-white" />}
              stat={{ number: "4000+", description: "Refugees Helped" }}
            >
              <p className="text-textPrimary text-bodyRegular">
                Advocacy is the heartbeat of our work. We seek to amplify refugee voices and drive meaningful change through strategic partnerships and community events. From the annual Refugee Health Fair, where we collaborate by volunteering with the medical students of Baylor College of Medicine, UT McGovern, UH medical school, to the Refugee Mental Health and Wellness Conference, where we help to facilitate guest speakers and workshops, our mission is clear: to create awareness about the global refugee crisis and how students can be apart of the solution.
              </p>
            </PillarSection>
          </section>
          
          {/* Our Commitment Section */}
          <motion.div 
            className="bg-white rounded-xl p-8 md:p-10 shadow-md mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col md:flex-row gap-10 items-center">
              <div className="md:w-1/2">
                <h3 className="font-display text-h3 text-secondary mb-4">Our Commitment</h3>
                <p className="text-textPrimary mb-6">
                  Students for Refugees stands on three unshakable pillars: Education, Action, and Advocacy.
                  Together, these principles guide our work as we strive to better the condition of refugees and,
                  more specifically, refugee students. We believe in the potential of this generation—our peers—and we aim to empower them to rise.
                </p>
                <p className="text-textPrimary mb-6">
                  We are ever-evolving, always seeking new opportunities to expand our impact. If you represent an organization and wish to partner with us, we invite you to connect through our contact page. Let us build something meaningful together for our future.
                </p>
                <Link to="/contact" className="inline-flex items-center text-cta font-bold hover:underline">
                  Connect with us
                  <ArrowRight size={16} className="ml-2" />
                </Link>
              </div>
              <div className="md:w-1/2 grid grid-cols-2 gap-4">
                <div className="bg-lightTeal p-4 rounded-lg text-center">
                  <div className="font-display text-h2 text-cta">1,000+</div>
                  <div className="text-textPrimary">Tutoring Hours</div>
                </div>
                <div className="bg-lightTeal p-4 rounded-lg text-center">
                  <div className="font-display text-h2 text-cta">50+</div>
                  <div className="text-textPrimary">Active Tutors</div>
                </div>
                <div className="bg-lightTeal p-4 rounded-lg text-center">
                  <div className="font-display text-h2 text-cta">4000+</div>
                  <div className="text-textPrimary">Refugees Helped</div>
                </div>
                <div className="bg-lightTeal p-4 rounded-lg text-center">
                  <div className="font-display text-h2 text-cta">3</div>
                  <div className="text-textPrimary">Focus Areas</div>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* CTA Section */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-h3 text-secondary mb-8">Join Us in Making an Impact</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <ScrollToTopLink to="/donate">
                <motion.button
                  className="bg-cta text-white py-3 px-8 rounded-xl text-button font-body shadow-md flex items-center justify-center w-full sm:w-auto"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Heart size={20} className="mr-2" />
                  Support Our Mission
                </motion.button>
              </ScrollToTopLink>
              <ScrollToTopLink to="/volunteer/calendar">
                <motion.button
                  className="bg-primary text-white py-3 px-8 rounded-xl text-button font-body shadow-md flex items-center justify-center w-full sm:w-auto"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Clock size={20} className="mr-2" />
                  Get Involved
                </motion.button>
              </ScrollToTopLink>
            </div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
      <StickyBottomNav />
    </div>
  );
}

export default Impact;